import { FC, useEffect, useMemo } from "react";
import { match } from "ts-pattern";
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Stack,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Divider,
  type SxProps,
  type Theme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useCompanyPageStore } from "@/store/companyPageStore";
import { useGetCompanyIntentContextLazyQuery } from "@/generated/company-search";
import { COMPANY_SEARCH_CLIENT } from "@/utils/consts";
import { lightScrollbarStyle } from "@/styles/lightScrollbarStyle";
import {
  CONTEXT_TYPE_DESCRIPTIONS,
  CONTEXT_TYPES,
  type ContextTypeName,
} from "@/lib/company-filter/modules/intent/constants";
import type { IntentContext } from "./types";

const isKnownContextType = (name: string): name is ContextTypeName =>
  CONTEXT_TYPES.includes(name as ContextTypeName);

const styles = {
  paper: {
    position: "absolute",
    top: 2,
    right: 2,
    width: 300,
    height: "calc(100% - 4px)",
    overflow: "hidden",
    borderRadius: 2,
    display: "flex",
    flexDirection: "column",
    zIndex: 1,
  } as const,
  paperOpen: {
    transform: "translateX(0)",
    transition: "transform 200ms ease-out",
  } as const,
  paperClosed: {
    transform: "translateX(calc(100% + 8px))",
    transition: "transform 200ms ease-out",
  } as const,
  headerTitle: {
    flex: 1,
  } satisfies SxProps<Theme>,
  content: {
    flexGrow: 1,
    overflow: "auto",
    ...lightScrollbarStyle,
    p: 2,
  } satisfies SxProps<Theme>,
  listItem: {
    pl: 0,
  } satisfies SxProps<Theme>,
};

/**
 * Sliding sidebar that displays intent signal context details
 * Shows products and vendors grouped by context type
 */
export const IntentSignalDetails: FC = () => {
  const intentSignalDetailsOpen = useCompanyPageStore(
    (state) => state.intentSignalDetailsOpen
  );
  const setIntentSignalDetailsOpen = useCompanyPageStore(
    (state) => state.setIntentSignalDetailsOpen
  );
  const selectedIntentSignal = useCompanyPageStore(
    (state) => state.selectedIntentSignal
  );
  const setSelectedIntentSignal = useCompanyPageStore(
    (state) => state.setSelectedIntentSignal
  );

  const [fetchContexts, { loading, data, error }] =
    useGetCompanyIntentContextLazyQuery({
      context: { clientName: COMPANY_SEARCH_CLIENT },
      fetchPolicy: "cache-and-network",
    });

  // Fetch context details when signal is selected
  useEffect(() => {
    if (selectedIntentSignal?.contextAggregate?.contextIds?.length) {
      fetchContexts({
        variables: {
          ids: selectedIntentSignal.contextAggregate.contextIds,
          urlId: selectedIntentSignal.urlId,
          topicId: selectedIntentSignal.topic.id,
          page: 0,
          per: selectedIntentSignal.contextAggregate.contextIds.length,
          sort: [],
        },
      });
    }
  }, [selectedIntentSignal, fetchContexts]);

  const handleClose = () => {
    setIntentSignalDetailsOpen(false);
    setSelectedIntentSignal(null);
  };

  // Group contexts by type name (Displacement, Whitespace, etc.)
  // Within each group, separate products and vendors
  const groupedContexts = useMemo(() => {
    const contexts = (data?.intentContext?.data ?? []) as IntentContext[];
    const groups: Record<string, { products: string[]; vendors: string[] }> =
      {};

    contexts.forEach((ctx) => {
      const typeName = ctx.contextType?.name ?? "Other";
      if (!groups[typeName]) {
        groups[typeName] = { products: [], vendors: [] };
      }
      if (ctx.productName) {
        groups[typeName].products.push(ctx.productName);
      }
      if (ctx.vendorName) {
        groups[typeName].vendors.push(ctx.vendorName);
      }
    });

    // Deduplicate, sort (case-insensitive), and filter out empty groups
    const result: typeof groups = {};
    const caseInsensitiveSort = (a: string, b: string) =>
      a.localeCompare(b, undefined, { sensitivity: "base" });
    Object.keys(groups).forEach((key) => {
      const products = [...new Set(groups[key].products)].sort(
        caseInsensitiveSort
      );
      const vendors = [...new Set(groups[key].vendors)].sort(
        caseInsensitiveSort
      );
      // Only keep groups that have at least one product or vendor
      if (products.length > 0 || vendors.length > 0) {
        result[key] = { products, vendors };
      }
    });

    return result;
  }, [data]);

  // Compute content status as a discriminated union for exhaustive matching
  // This prevents: loading hiding cached data, empty flash on first render
  type ContentStatus = "idle" | "loading" | "error" | "empty" | "success";
  const hasContextIds =
    !!selectedIntentSignal?.contextAggregate?.contextIds?.length;
  const hasGroupedData = Object.keys(groupedContexts).length > 0;

  const getContentStatus = (): ContentStatus => {
    if (!selectedIntentSignal) return "idle";
    if (loading && !hasGroupedData) return "loading";
    if (error && !hasGroupedData) return "error";
    if (!hasContextIds || !hasGroupedData) return "empty";
    return "success";
  };
  const contentStatus = getContentStatus();

  const topicName =
    selectedIntentSignal?.topic?.displayName ?? "Intent Details";

  return (
    <Paper
      elevation={8}
      sx={{
        ...styles.paper,
        ...(intentSignalDetailsOpen ? styles.paperOpen : styles.paperClosed),
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        p={2}
      >
        <Typography variant="h6" noWrap sx={styles.headerTitle}>
          {topicName}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </Stack>

      <Divider />

      {/* Content */}
      <Box sx={styles.content}>
        {match(contentStatus)
          .with("idle", () => null)
          .with("loading", () => (
            <Stack spacing={1}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="text" height={32} />
              ))}
            </Stack>
          ))
          .with("error", () => (
            <Typography color="error" variant="body2">
              Failed to load context details
            </Typography>
          ))
          .with("empty", () => (
            <Typography color="text.secondary" variant="body2">
              No Context Data for this Intent signal
            </Typography>
          ))
          .with("success", () =>
            Object.entries(groupedContexts).map(
              ([typeName, { products, vendors }], index, arr) => (
                <Box key={typeName}>
                  {/* Context Type Header */}
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    {typeName}
                  </Typography>
                  {isKnownContextType(typeName) && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {CONTEXT_TYPE_DESCRIPTIONS[typeName]}
                    </Typography>
                  )}

                  {/* Detected Products */}
                  {products.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        display="block"
                      >
                        Detected Products
                      </Typography>
                      <List dense disablePadding>
                        {products.map((name) => (
                          <ListItem
                            key={name}
                            disablePadding
                            sx={styles.listItem}
                          >
                            <ListItemText
                              primary={name}
                              primaryTypographyProps={{ variant: "body2" }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Detected Vendors */}
                  {vendors.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        display="block"
                      >
                        Detected Vendors
                      </Typography>
                      <List dense disablePadding>
                        {vendors.map((name) => (
                          <ListItem
                            key={name}
                            disablePadding
                            sx={styles.listItem}
                          >
                            <ListItemText
                              primary={name}
                              primaryTypographyProps={{ variant: "body2" }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Divider between sections */}
                  {index < arr.length - 1 && <Divider sx={{ my: 2 }} />}
                </Box>
              )
            )
          )
          .exhaustive()}
      </Box>
    </Paper>
  );
};
